import {
  PIPING_SPEC_OPTIONS,
  MATERIAL_OPTIONS,
  DESIGN_STANDARD_OPTIONS,
  PRESSURE_CLASS_OPTIONS,
  INSULATION_SPEC_OPTIONS,
  INSULATION_THICKNESS_OPTIONS,
  TRACING_SPEC_OPTIONS,
  ITEM_DESCRIPTION_OPTIONS,
} from '../../../shared/config/dropdownOptions';
import {
  ETALON_OBJECT_TYPE_OPTIONS,
  ETALON_SKEY_OPTIONS,
  ETALON_END_TYPE_OPTIONS,
  normalizeObjectTypeKey,
} from '../../../shared/config/pcfEtalonCatalog';
import { filterObjectTypeOptionsForCategories } from '../../../shared/config/objectTypeCategoryCompatibility';
import { AUTO_FILL_PATTERNS } from '../../../shared/config/autoFillPatterns';

export interface QuickFillValueOption {
  value: string;
  label: string;
}

const AUTO_FILL_HELPER_OBJECT_TYPES = [
  'FIELD-WELD',
  'REFERENCE-POINT',
  'END-CONNECTION-EQUIPMENT',
  'FLOW-ARROW',
  'FLOOR-SYMBOL',
  'REINFORCEMENT-PAD',
  'MISC-COMPONENT',
];

const AUTO_FILL_HELPER_SKEYS = [
  'WF',
  'WW',
  'NIPPLE',
  'WAL5',
  'PIP4',
  'BLD2',
  'EQU2',
  'FLR5',
  'XXX4',
  'HST4',
  'VST4',
];

function isQuickFillCellValue(value: string): boolean {
  const normalized = value.trim().toUpperCase();
  return normalized !== '' && !normalized.startsWith('#PCF_');
}

function toOption(value: string): QuickFillValueOption {
  return { value, label: value };
}

function mergeValueOptions(values: string[]): QuickFillValueOption[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(isQuickFillCellValue)))
    .sort((left, right) => left.localeCompare(right))
    .map(toOption);
}

export const QUICK_FILL_OBJECT_TYPE_OPTIONS: QuickFillValueOption[] = mergeValueOptions([
  ...ETALON_OBJECT_TYPE_OPTIONS.map((option) => option.value),
  ...AUTO_FILL_PATTERNS.map((pattern) => normalizeObjectTypeKey(pattern.objectType)),
  ...AUTO_FILL_HELPER_OBJECT_TYPES,
]);

export const QUICK_FILL_SKEY_OPTIONS: QuickFillValueOption[] = mergeValueOptions(
  [
    ...ETALON_SKEY_OPTIONS.map((option) => option.value),
    ...AUTO_FILL_PATTERNS.map((pattern) => pattern.skey),
    ...AUTO_FILL_HELPER_SKEYS,
  ]
);

export const QUICK_FILL_END_TYPE_OPTIONS: QuickFillValueOption[] =
  ETALON_END_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label }));

export function getQuickFillValueOptions(label: string): QuickFillValueOption[] {
  switch (label) {
    case 'SKEY':
      return QUICK_FILL_SKEY_OPTIONS;
    case 'OBJECT-TYPE':
      return QUICK_FILL_OBJECT_TYPE_OPTIONS;
    case 'End1':
    case 'End2':
    case 'End3':
    case 'End4':
      return QUICK_FILL_END_TYPE_OPTIONS;
    case 'PIPING-SPEC':
      return PIPING_SPEC_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
    case 'MATERIAL':
      return MATERIAL_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
    case 'DESIGN-STD':
      return DESIGN_STANDARD_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
    case 'PRESSURE-CLASS':
      return PRESSURE_CLASS_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
    case 'INSULATION-SPEC':
      return INSULATION_SPEC_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
    case 'INSULATION-THICKNESS':
      return INSULATION_THICKNESS_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
    case 'TRACING-SPEC':
      return TRACING_SPEC_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
    case 'ITEM-DESCRIPTION':
      return ITEM_DESCRIPTION_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
    default:
      return [];
  }
}

export function getQuickFillValueOptionsForCategories(
  label: string,
  categories: Array<string | undefined | null>
): QuickFillValueOption[] {
  const options = getQuickFillValueOptions(label);
  if (label !== 'OBJECT-TYPE') {
    return options;
  }

  return filterObjectTypeOptionsForCategories(options, categories);
}
